import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError as observableThrowError } from 'rxjs';
import { tap } from 'rxjs/operators';
import { BaseWrapperService } from '../../shared/base-service/base-wrapper.service';
import { NewBusinessTransaction } from '../../transaction/models/new-business-transaction.model';
import { VehicleAddressModel } from '../../vehicle/models/vehicle-address.model';
import { VehicleModel } from '../../vehicle/models/vehicle.model';
import { CustomerPlateModel } from '../models/customer-plate.model';
import { DuplicateTitleModel } from '../models/duplicate-title.model';
import { LienCustomerModel } from '../models/lien-customer.model';
import { LienModel } from '../models/lien.model';
import { OwnershipCustomerRelationshipModel } from '../models/ownership-customer-relationship.model';
import { RegistrationModel } from '../models/registration.model';
import { TitleCancelModel } from '../models/title-cancel.model';
import { TitleOutOfJurisdictionModel } from '../models/title-out-of-jurisdiction.model';
import { TitleModel } from '../models/title.model';
import { VehicleEmissionCheckStatusRegistrationModel } from '../models/vehicle-emission-check-status-registration.model';
import { VehicleOwnershipModel } from '../models/vehicle-ownership.model';
import { VehicleUseTaxModel } from '../models/vehicle-use-tax.model';
import { TabToTabService } from '../../shared/services/tab-to-tab-messaging/tab-to-tab.service';
import { TabToTabChannelName } from '../../shared/enums/tab-to-tab-channel-name.enum';
import { PermanentPlacardTransactionModel } from '../models/permanent-placard-transaction.model';

@Injectable()
export class TransactionService extends BaseWrapperService {

    constructor(private tabToTabService: TabToTabService){
        super();
    }

    /**
     * Creates a basic registration renew model.
     * @return Basic registration renew model.
     */
    createRegistrationRenew = (vehicleId: number) => this.get('TitleRegistration/Registration/Renew/Create/' + vehicleId);

    /**
     * Creates a basic suspend registration model.
     * @return Basic suspend registration model.
     */
    createSuspendRegistration = (registrationId: number) => this.get('TitleRegistration/Registration/Suspend/Create/' + registrationId);

    /**
     * Creates a duplicate registration model.
     * @return registration model.
     */
    createDuplicateRegistration = (registrationId: number) => this.get('TitleRegistration/Registration/Duplicate/Create/' + registrationId);

    /**
     * Creates a basic title and registration model.
     * @return Basic title and registration model.
     */
    createTitleRegistrationWithOptions = (customerNumberId: number, vehicleId: number, agentId: number, addTitle: boolean, addRegistration: boolean) => this.get('TitleRegistration/TitleRegistration/Create/AddTitle/' + addTitle + '/AddRegistration/' + addRegistration + '/Customer/' + customerNumberId + '/Vehicle/' + vehicleId + '/Agent/' + agentId);

    /**
     * Creates a temporary placard model.
     * @return Temporary placard model.
     */
    createTemporaryPlacard = () => this.get('TitleRegistration/Placard/Temporary/Create/');

    /**
     * Creates a permanent placard model.
     * @return Permanent placard model.
     */
    createPermanentPlacard = () => this.get('TitleRegistration/Placard/Permanent/Create/');

    /**
     * Creates a renew permanent placard model.
     * @return Permanent placard model.
     */
    createRenewPermanentPlacard = () => this.get('TitleRegistration/Placard/Permanent/Renew/Create/');

    /**
     * Creates a replace permanent placard model.
     * @return Permanent placard model.
     */
    createReplacePermanentPlacard = (placardNumber: string) => this.get('TitleRegistration/Placard/Permanent/Replace/' + placardNumber + '/Create/');

    /**
     * Creates a replace temporary placard model.
     * @return Temporary placard model.
     */
    createReplaceTemporaryPlacard = (placardNumber: string) => this.get('TitleRegistration/Placard/Temporary/Replace/' + placardNumber + '/Create/');

    /**
     * Creates a cancel permanent placard model.
     * @return Permanent placard model.
     */
    createCancelPermanentPlacard = (placardNumber: string) => this.get('TitleRegistration/Placard/Permanent/Cancel/' + placardNumber + '/Create/');

    /**
     * Creates a cancel temporary placard model.
     * @return Temporary placard model.
     */
    createCancelTemporaryPlacard = (placardNumber: string) => this.get('TitleRegistration/Placard/Temporary/Cancel/' + placardNumber + '/Create/');

    /**
     * Creates a business transaction
     * @param model: Business model that will be stored as the base type for the transaction.
     * @param transactionTypeId: Transaction type identifier.
     * @return Business transaction id for the newly created transaction.
     */
    saveTransaction = (model: any, transactionTypeId: number) => this.post('TitleRegistration/BusinessTransaction/Type/' + transactionTypeId, model);

    /**
     * Updates the transaction in WIP.
     * @param model: Model that contains the information to save to Wip.
     * @param businessTransactionId: Business transaction id.
     * @return True if the call succeeded.  Otherwise, false.
     */
    updateBusinessTransaction = (model: any, businessTransactionId: number, businessTransactionTypeId: number) => this.post('TitleRegistration/BusinessTransaction/' + businessTransactionId + '/Type/' + businessTransactionTypeId, model);

    /**
    * Updates the transaction in WIP.
    * @param model: Model that contains the information to save to Wip.
    * @param businessTransactionId: Business transaction id.
    * @return True if the call succeeded.  Otherwise, false.
    */
    updateAndValidateBusinessTransaction = (model: any, businessTransactionId: number, businessTransactionTypeId: number) => this.post('TitleRegistration/BusinessTransaction/' + businessTransactionId + '/Type/' + businessTransactionTypeId + '/Validate', model);

    /**
     * Updates the transaction in WIP.
     * @param model: Model that contains the information to save to Wip.
     * @param businessTransactionId: Business transaction id.
     * @return True if the call succeeded.  Otherwise, false.
     */
    performBusinessTransactionAction = (action: string, model: any, businessTransactionId: number, businessTransactionTypeId: number) => this.post('TitleRegistration/BusinessTransaction/Action/' + action + '/' + businessTransactionId + '/Type/' + businessTransactionTypeId, model);

    /**
   * Gets the current ownership for the vehicle
   * @param vehicleId: Vehicle to get the current ownership for.
   */
    getActiveTitleByVehicle = (vehicleId: number) => this.get('TitleRegistration/Title/Vehicle/' + vehicleId + '/Active');

    /**
     * Gets the object in the business transaction
     * @param businessTransactionId: Business transaction id to get the object for.
     * @return Object stored in Wip for the business transaction id.
     */
    getTransaction = (businessTransactionId: number) => this.get('TitleRegistration/BusinessTransaction/' + businessTransactionId);

    getPlateOrderBusinessTransaction = (businessTransactionId: number) => this.get('TitleRegistration/BusinessTransaction/PlateOrder/BusinessTransaction/' + businessTransactionId);

    getVehicleDomicileAddress = (businessTransactionId: number) => this.get('TitleRegistration/TitleRegistration/Vehicle/VehicleDomicileAddress/' + businessTransactionId);

    canEditVehicleDomicileAddress = (businessTransactionId: number) => this.get('TitleRegistration/TitleRegistration/Vehicle/VehicleDomicileAddress/CanEdit/' + businessTransactionId);

    /**
     * Gets the object in the business transaction
     * @param businessTransactionId: Business transaction id to get the object for.
     * @return Object stored in Wip for the business transaction id.
     */
    getRegistrationTransaction = (businessTransactionId: number) => this.get('TitleRegistration/Registration/' + businessTransactionId);

    /**
     * Gets the transaction type for the given business transaction identifier.
     * @param businessTransactionId: Id of the transaction to get the transaction type for.
     * @return Transaction Type Id.
     */
    getTransactionTypeByBusinessTransactionId = (businessTransactionId: number) => this.get('Foundation/BusinessTransaction/' + businessTransactionId + '/TransactionType/Id');

    /**
     * Updates the vehicle ownership roles on the given business transaction.
     * @param model: VehicleOwnershipRole array that contains the information to save to Wip.
     * @param businessTransactionId: Business transaction id.
     * @return True if the call succeeded.  Otherwise, false.
     */
    updateVehicleOwnershipRoles = (model: OwnershipCustomerRelationshipModel[], businessTransactionId: number) => this.post('TitleRegistration/Ownership/OwnershipCustomerRelationships/Trn/' + businessTransactionId, model);

    /**
     * Gets the ownership relationships on a transaction.
     * @param businessTransactionId: Id of the transaction to get the transaction type for.
     * @return: List of customer relationships.
     */
    getOwnershipCustomerRelationships = (businessTransactionId: number) => this.get('TitleRegistration/Ownership/OwnershipCustomerRelationships/Trn/' + businessTransactionId);

    /**
     * Updates vehicle information to wip.
     * @param businessTransactionNumberId: Business transaction number Id.
     * @param vehicleId: Vehicle Id.
     */
    updateVehicle = (businessTransactionId: number, vehicle: VehicleModel) => this.post('TitleRegistration/TitleRegistration/Vehicle/Trn/' + businessTransactionId, vehicle);

    /**
     * Updates vehicle information to wip.
     * @param businessTransactionNumberId: Business transaction number Id.
     * @param vehicleId: Vehicle Id.
     */
    updateVehicleBrands = (businessTransactionId: number, vehicle: VehicleModel) => this.post('TitleRegistration/TitleRegistration/Vehicle/Brands/Trn/' + businessTransactionId, vehicle);

    updateIRPVehicleBrands = (businessTransactionId: number, vehicle: VehicleModel) => this.post('IRP/MotorCarrier/Vehicle/Brands/Transaction/' + businessTransactionId, vehicle);

    /**
     * Updates the liens on the given business transaction.
     * @param model: Lien array that contains the information to save to Wip.
     * @param businessTransactionId: Business transaction id.
     * @return True if the call succeeded.  Otherwise, false.
     */
    updateLiens = (model: LienModel[], businessTransactionId: number) => this.post('TitleRegistration/Lien/All/Trn/' + businessTransactionId, model);

    /**
     * Updates the lien holders on the given business transaction.
     * @param model: LienCustomerModel array that contains the information to save to Wip.
     * @param businessTransactionId: Business transaction id.
     * @return True if the call succeeded.  Otherwise, false.
     */
    updateLienCustomers = (model: LienCustomerModel[], lienId: number, businessTransactionId: number) => this.post('TitleRegistration/Lien/' + lienId + '/Holders/Trn/' + businessTransactionId, model);

    /**
     * Updates the current vehicle ownership on the given business transaction.
     * @param model: VehicleOwnershipModelOld that contains the information to save to Wip.
     * @param businessTransactionId: Business transaction id.
     * @return True if the call succeeded.  Otherwise, false.
     */
    updateCurrentOwnership = (model: VehicleOwnershipModel, businessTransactionId: number) => this.post('TitleRegistration/Ownership/Trn/' + businessTransactionId, model);

    /**
     * Updates the previous title on the given business transaction.
     * @param model: TitleModel that contains the information to save to Wip.
     * @param businessTransactionId: Business transaction id.
     * @return True if the call succeeded.  Otherwise, false.
     */
    updatePreviousTitle = (model: TitleModel, businessTransactionId: number) => this.post('TitleRegistration/Title/Previous/Trn/' + businessTransactionId, model);

    /**
     * Updates the current title on the given business transaction.
     * @param model: TitleModel that contains the information to save to Wip.
     * @param businessTransactionId: Business transaction id.
     * @return True if the call succeeded.  Otherwise, false.
     */
    updateCurrentTitle = (model: TitleModel, businessTransactionId: number) => this.post('TitleRegistration/Title/Trn/' + businessTransactionId, model);

    /**
     * Updates the current vehicle ownership on the given business transaction.
     * @param model: VehicleOwnershipModelOld that contains the information to save to Wip.
     * @param businessTransactionId: Business transaction id.
     * @return True if the call succeeded.  Otherwise, false.
     */
    updateVehicleUseTax = (model: VehicleUseTaxModel, businessTransactionId: number) => this.post('TitleRegistration/Ownership/Trn/VehicleSalesTax/' + businessTransactionId, model);

    /**
     * Updates the out of jurisdiction title on the given business transaction.
     * @param model: TitleOutOfJurisdictionModel that contains the information to save to Wip.
     * @param businessTransactionId: Business transaction id.
     * @return True if the call succeeded.  Otherwise, false.
     */
    updateTitleOutOfJurisdiction = (model: TitleOutOfJurisdictionModel, businessTransactionId: number) => this.post('TitleRegistration/Title/OutOfJurisdiction/Trn/' + businessTransactionId, model);

    /**
     * Updates the temporary placard on the given business transaction.
     * @param model: CustomerPlateModel that contains the information to save to Wip.
     * @param businessTransactionId: Business transaction id.
     * @return True if the call succeeded.  Otherwise, false.
     */
    updatePlacard = (model: CustomerPlateModel, businessTransactionId: number) => this.post('TitleRegistration/Placard/Trn/' + businessTransactionId, model);

    /**
     * Updates the permanent placard transaction to WIP.
     * @param model: PermanentPlacardTransactionModel to save to Wip.
     * @param businessTransactionId: Business transaction id.
     * @return True if the call succeeded.  Otherwise, false.
     */
    updatePermanentPlacardTransaction = (model: PermanentPlacardTransactionModel, businessTransactionId: number) => this.post('TitleRegistration/Placard/Trn/' + businessTransactionId + '/Permanent/Update', model);

    /**
     * Updates the vehicle emission on the given business transaction.
     * @param model: VehicleEmissionCheckStatusRegistrationModel that contains the information to save to Wip.
     * @param businessTransactionId: Business transaction id.
     * @return True if the call succeeded.  Otherwise, false.
     */
    updateVehicleEmissions = (model: VehicleEmissionCheckStatusRegistrationModel, businessTransactionId: number) => this.post('TitleRegistration/Registration/Emission/Trn/' + businessTransactionId, model);

    /**
     * Updates the duplicate title on the given business transaction.
     * @param model: DuplicateTitleModel that contains the information to save to Wip.
     * @param businessTransactionId: Business transaction id.
     * @return True if the call succeeded.  Otherwise, false.
     */
    updateDuplicateTitle = (model: DuplicateTitleModel, businessTransactionId: number, businessTransactionTypeId: number) => this.post('TitleRegistration/BusinessTransaction/' + businessTransactionId + '/Type/' + businessTransactionTypeId, model);

    /**
    * Validates the current transaction.
    */
    validateTransaction = (businessTransactionId: number) => {
        if (businessTransactionId) {
            return this.get('TitleRegistration/BusinessTransaction/' + businessTransactionId + '/Validate');
        }
    };

    /**
     * Validates the fees on the transaction
     */
    validateFees = (businessTransactionId: number) => this.get('TitleRegistration/BusinessTransaction/' + businessTransactionId + '/Fees/Validate');

    /**
     * Calculates fees for the current transaction.
     */
    calculateFees = (context: any) => {
        if (context.businessTransactionId && context.transactionTypeId) {
            return this.get('TitleRegistration/BusinessTransaction/' + context.businessTransactionId + '/Type/' + context.transactionTypeId + '/CalculateFees');
        }
    };

    /**
     * Creates an issue permit transaction.
     * @param context: N/A
     */
    createIssuePermit = (context: any) => this.get('TitleRegistration/BusinessTransaction/CreateIssuePermitTransaction');

    /**
     * Sets the correct mail to customers on an ownership for a transaction.
     * @param businessTransactionId: Business transaction number identifier.
     */
    setMailTo = (businessTransactionId: number) => this.get('TitleRegistration/Ownership/MailTo/Trn/' + businessTransactionId);

    /**
     * Validates the address for emissions.
     * @param address: Address to check
     */
    validateAddressForEmission = (address: VehicleAddressModel) => this.post('Address/Validation', address);

    /**
     * Processes the cancel title request.
     * @param cancelTitles: List of titles to cancel.
     */
    processCancelTitles = (cancelTitles: TitleCancelModel[]) => this.post('TitleRegistration/Title/Cancel', cancelTitles);

    /**
     * Checks to see if a vehicle's active registration is an ARO or SPI registration.
     * @param vehicleId: Vehicle on the active registration.
     * @returns: True if the vehicle's active registration is an ARO or SPI.  Otherwise, false.
     */
    isRegistrationAROSPI = (vehicleId: number): Observable<boolean> => this.get('TitleRegistration/Registration/AROSPI/' + vehicleId);

    /**
     * Gets the active registration for the vehicle and loads it as the registration for the transaction in WIP.
     * @param vehicleId: Vehicle the registration is associated to.
     * @param businessTransactionId: Business transaction number identifier.
     * @returns: Registration that was added as a registration to the transaction.
     */
    setActiveRegistrationForVehicle = (vehicleId: number, businessTransactionId): Observable<RegistrationModel> => this.get('TitleRegistration/Registration/Load/Vehicle/' + vehicleId + '/Trn/' + businessTransactionId);

    /**
     * Checks to see if the vehicle is currently only titled with no registration
     * @param vehicleId: Vehicle to check
     * @returns: True if the vehicle is title only.  Otherwise, false.
     */
    checkIsTitleOnly(vehicleId: number): Observable<boolean> {
        return this.get('TitleRegistration/Title/Only/Check/' + vehicleId);
    }

    /**
     * Gets the active title for the vehicle and loads it as the title for the transaction in WIP.
     * @param vehicleId: Vehicle the title is associated to.
     * @param businessTransactionId: Business transaction number identifier.
     * @returns: Title that was added as a title to the transaction.
     */
    setActiveTitleForVehicle = (vehicleId: number, businessTransactionId): Observable<TitleModel> => this.get('TitleRegistration/Title/Load/Vehicle/' + vehicleId + '/Trn/' + businessTransactionId);

    private handleError(error: HttpErrorResponse) {
        return observableThrowError(error.error || 'Server error');
    }

    /**
     * Creates a business transaction
     * @param model: Business model that will be stored as the base type for the transaction.
     * @param transactionTypeId: Transaction type identifier.
     * @return Business transaction id for the newly created transaction.
     */
    setOneTimeMailingAddresses = (model: any, businessTransactionId: number) => this.post('TitleRegistration/BusinessTransaction/' + businessTransactionId + '/OneTimeMailing/', model);

    /**
     * Gets the list of fees for a business transaction along with support data.
     * @param businessTransactionId: Business transaction with fee data.
     * @return List of fees and support data for the fees
     */
    getFeeSelectionData = (businessTransactionId: number) => this.get('TitleRegistration/BusinessTransaction/' + businessTransactionId + '/Fees');

    /**
     * Selects the registration cycle for the transaction based on the desired duration.
     * @param businessTransactionNumberId: Business transaction to set the cycle for.
     * @param registrationDurationTypeId: Registration duration type selected
     */
    selectRegistrationCycle = (businessTransactionId: number, registrationDurationTypeId: number) => this.post('TitleRegistration/Registration/Trn/' + businessTransactionId + '/Cycle/' + registrationDurationTypeId, null);

    checkin = (businessTransactionIds: number[], agentId: number, newBusinessTransactions: NewBusinessTransaction[]) => {
        const data = { BusinessTransactionIds: businessTransactionIds, AgentId: agentId, BusinessTransactionNewServiceDetails: newBusinessTransactions };
        return this.post('Foundation/BusinessTransaction/CheckIn', data).pipe(tap(_ => {
            try {
                this.tabToTabService.sendMessage(TabToTabChannelName.ActiveBusinessTransaction, {});
            } catch (error) {
                console.error(error);
            }
        }));
    };

    /**
     * Gets a boolean value that determines if the override criteria has been answered already
     * @param businessTransactionId: Business transaction to check
     * @return True if the criteria has been previously met.  Otherwise, false.
     */
    overrideQuestionAnswered = (businessTransactionId: number) => this.get('TitleRegistration/BusinessTransaction/' + businessTransactionId + '/OverrideAnswered');

    /**
     * Sets the business transaction key of TROverrideAnswered for the business transaction.
     * @param businessTransactionId: Business transaction to set as having answered the prompt.
     */
    setOverrideQuestionAnswerKey = (businessTransactionId: number) => this.post('TitleRegistration/BusinessTransaction/' + businessTransactionId + '/OverrideAnswered', null);

    /**
      * Gets the customer to receive the credit for Request Registration Credit transaction
      * @param businessTransactionId: Business transaction to set as having answered the prompt.
      */
    getCustomerForRequestRegistrationCredit = (businessTransactionId: number) => this.get('TitleRegistration/Ownership/RequestRegistrationCredit/' + businessTransactionId + '/Customer');

    updateCustomerForRequestRegistrationCredit = (primaryCustomerNumberId: number, businessTransactionId: number) => this.post('TitleRegistration/Ownership/RequestRegistrationCredit/' + businessTransactionId + '/Customer/' + primaryCustomerNumberId, null);

    /**
     * Get's an address by id
     */
    getAddressByVehicleAddressId = (addressNumberId: number) => this.get('Address/address/GetVehicleAddress/' + addressNumberId);

    updateEmissionsCheck = (businessTransactionId: number) => this.get('TitleRegistration/Registration/Emission/' + businessTransactionId + '/Update');

    getActiveTRP = (vehicleId: number) => this.get('TitleRegistration/TitleRegistration/TRP/' + vehicleId + '/Active');

    /**
    * Checks to see if the transaction type warrants a sync of the customer data, and if it does, syncs the WIP with the updated customer data.
    * @param businessTransactionId: Business transaction to update.
    */
    updateOwners = (businessTransactionId: number) => this.post('TitleRegistration/BusinessTransaction/' + businessTransactionId + '/UpdateOwners', null);
}
